import { clampFrame } from "../core/time";

export type PlaybackState = {
  currentFrame: number;
  playing: boolean;
  buffering: boolean;
  error: string | null;
};

const HAVE_METADATA = 1;
const DEFAULT_STALL_TIMEOUT_MS = 10_000;

/**
 * Keeps one media element for the lifetime of an editor. Safari grants media
 * playback permission per element, so recovery replaces the Blob URL on this
 * element rather than constructing a new Audio element.
 */
export class PlaybackController {
  private readonly audio: HTMLAudioElement;
  private readonly stallTimeoutMs: number;
  private sourceFile: File | null = null;
  private objectUrl: string | null = null;
  private animationFrame = 0;
  private stallTimer: number | null = null;
  private preview: { start: number; end: number; loop: boolean } | null = null;
  private listener: ((state: PlaybackState) => void) | null = null;
  private sampleRate = 48_000;
  private durationFrames = 0;
  private trackStartSeconds = 0;
  private desiredFrame = 0;
  private buffering = false;
  private errorMessage: string | null = null;
  private recoveryRequired = false;
  private playAttempt = 0;

  constructor(
    audio: HTMLAudioElement = new Audio(),
    stallTimeoutMs = DEFAULT_STALL_TIMEOUT_MS,
  ) {
    this.audio = audio;
    this.stallTimeoutMs = stallTimeoutMs;
    this.audio.preload = "metadata";
    this.audio.addEventListener("loadedmetadata", this.loadedMetadata);
    this.audio.addEventListener("playing", this.startedPlaying);
    this.audio.addEventListener("waiting", this.startedBuffering);
    this.audio.addEventListener("stalled", this.startedBuffering);
    this.audio.addEventListener("seeking", this.startedBuffering);
    this.audio.addEventListener("seeked", this.finishedSeeking);
    this.audio.addEventListener("pause", this.stoppedPlaying);
    this.audio.addEventListener("ended", this.stoppedPlaying);
    this.audio.addEventListener("error", this.mediaFailed);
  }

  load(
    file: File,
    sampleRate: number,
    durationFrames: number,
    trackStartSeconds: number,
    currentFrame: number,
  ): void {
    this.cancelPendingPlayback();
    this.sourceFile = file;
    this.sampleRate = sampleRate;
    this.durationFrames = durationFrames;
    this.trackStartSeconds = trackStartSeconds;
    this.desiredFrame = clampFrame(currentFrame, durationFrames);
    this.preview = null;
    this.buffering = false;
    this.errorMessage = null;
    this.recoveryRequired = false;
    this.replaceSource();
    this.emit();
  }

  subscribe(listener: (state: PlaybackState) => void): () => void {
    this.listener = listener;
    this.emit();
    return () => {
      if (this.listener === listener) this.listener = null;
    };
  }

  async toggle(): Promise<void> {
    this.preview = null;
    if (!this.audio.paused || this.buffering) this.pause();
    else await this.play();
  }

  async play(): Promise<void> {
    const attempt = ++this.playAttempt;
    this.clearStallTimer();
    this.errorMessage = null;
    if (this.recoveryRequired || this.audio.error) {
      this.replaceSource();
      this.recoveryRequired = false;
    }
    if (!this.preview && this.currentFrame >= this.durationFrames - 1)
      this.seek(0);
    this.buffering = true;
    this.emit();
    this.armStallTimer();
    try {
      await this.audio.play();
      if (attempt !== this.playAttempt) return;
      this.buffering = false;
      this.clearStallTimer();
      this.emit();
    } catch (error) {
      if (attempt !== this.playAttempt) return;
      if (isAbortError(error) && this.audio.paused) return;
      this.failPlayback(playbackErrorMessage(error));
    }
  }

  pause(): void {
    this.cancelPendingPlayback();
    this.buffering = false;
    this.audio.pause();
    this.emit();
  }

  seek(frame: number): void {
    this.desiredFrame = clampFrame(frame, this.durationFrames);
    this.applyDesiredFrame();
    if (!this.audio.paused) {
      this.buffering = true;
      this.armStallTimer();
    }
    this.emit();
  }

  skip(seconds: number): void {
    this.seek(this.currentFrame + Math.round(seconds * this.sampleRate));
  }

  setRate(rate: number): void {
    this.audio.playbackRate = rate;
  }

  async playRange(
    startFrame: number,
    endFrame: number,
    loop: boolean,
  ): Promise<void> {
    const start = clampFrame(startFrame, this.durationFrames);
    const end = Math.max(start + 1, clampFrame(endFrame, this.durationFrames));
    this.preview = { start, end, loop };
    this.seek(start);
    await this.play();
  }

  stopPreview(): void {
    this.preview = null;
    this.pause();
  }

  get currentFrame(): number {
    if (this.audio.readyState < HAVE_METADATA) return this.desiredFrame;
    return clampFrame(
      (this.audio.currentTime - this.trackStartSeconds) * this.sampleRate,
      this.durationFrames,
    );
  }

  get playing(): boolean {
    return !this.audio.paused;
  }

  dispose(): void {
    this.cancelPendingPlayback();
    this.audio.pause();
    cancelAnimationFrame(this.animationFrame);
    this.audio.removeEventListener("loadedmetadata", this.loadedMetadata);
    this.audio.removeEventListener("playing", this.startedPlaying);
    this.audio.removeEventListener("waiting", this.startedBuffering);
    this.audio.removeEventListener("stalled", this.startedBuffering);
    this.audio.removeEventListener("seeking", this.startedBuffering);
    this.audio.removeEventListener("seeked", this.finishedSeeking);
    this.audio.removeEventListener("pause", this.stoppedPlaying);
    this.audio.removeEventListener("ended", this.stoppedPlaying);
    this.audio.removeEventListener("error", this.mediaFailed);
    this.releaseSource();
    this.sourceFile = null;
  }

  private readonly loadedMetadata = (): void => {
    this.applyDesiredFrame();
    this.emit();
  };

  private readonly startedPlaying = (): void => {
    this.buffering = false;
    this.errorMessage = null;
    this.clearStallTimer();
    this.startTicking();
  };

  private readonly startedBuffering = (): void => {
    if (this.audio.paused) return;
    this.buffering = true;
    this.armStallTimer();
    this.emit();
  };

  private readonly finishedSeeking = (): void => {
    if (this.audio.paused) {
      this.buffering = false;
      this.clearStallTimer();
    }
    this.emit();
  };

  private readonly stoppedPlaying = (): void => {
    cancelAnimationFrame(this.animationFrame);
    this.clearStallTimer();
    this.buffering = false;
    this.desiredFrame = this.currentFrame;
    this.emit();
  };

  private readonly mediaFailed = (): void => {
    this.failPlayback(mediaElementErrorMessage(this.audio.error));
  };

  private startTicking(): void {
    cancelAnimationFrame(this.animationFrame);
    const tick = (): void => {
      this.desiredFrame = this.currentFrame;
      if (this.preview && this.currentFrame >= this.preview.end) {
        if (this.preview.loop) this.seek(this.preview.start);
        else {
          this.preview = null;
          this.pause();
        }
      }
      this.emit();
      if (!this.audio.paused) this.animationFrame = requestAnimationFrame(tick);
    };
    tick();
  }

  private replaceSource(): void {
    const source = this.sourceFile;
    this.releaseSource();
    if (!source) return;
    this.objectUrl = URL.createObjectURL(source);
    this.audio.src = this.objectUrl;
    this.audio.preload = "metadata";
    this.audio.load();
    this.applyDesiredFrame();
  }

  private releaseSource(): void {
    const previousUrl = this.objectUrl;
    this.objectUrl = null;
    this.audio.removeAttribute("src");
    this.audio.load();
    if (previousUrl) URL.revokeObjectURL(previousUrl);
  }

  private applyDesiredFrame(): void {
    if (this.audio.readyState < HAVE_METADATA) return;
    const seconds =
      this.trackStartSeconds + this.desiredFrame / this.sampleRate;
    if (Math.abs(this.audio.currentTime - seconds) < 0.000_5) return;
    try {
      this.audio.currentTime = seconds;
    } catch {
      // A Blob media element may not accept a seek until loadedmetadata. The
      // desired integer frame remains queued and loadedMetadata retries it.
    }
  }

  private armStallTimer(): void {
    this.clearStallTimer();
    const startingFrame = this.currentFrame;
    this.stallTimer = window.setTimeout(() => {
      this.stallTimer = null;
      if (this.audio.paused || !this.buffering) return;
      const advancedFrames = Math.abs(this.currentFrame - startingFrame);
      if (advancedFrames >= this.sampleRate / 10) return;
      this.recoveryRequired = true;
      this.errorMessage =
        "この位置の再生準備に時間がかかったため停止しました。もう一度再生すると音声を再読み込みします。";
      this.cancelPendingPlayback();
      this.audio.pause();
      this.buffering = false;
      this.emit();
    }, this.stallTimeoutMs);
  }

  private clearStallTimer(): void {
    if (this.stallTimer !== null) window.clearTimeout(this.stallTimer);
    this.stallTimer = null;
  }

  private cancelPendingPlayback(): void {
    this.playAttempt += 1;
    this.clearStallTimer();
  }

  private failPlayback(message: string): void {
    this.clearStallTimer();
    this.buffering = false;
    this.recoveryRequired = true;
    this.errorMessage = message;
    this.audio.pause();
    this.emit();
  }

  private emit(): void {
    this.listener?.({
      currentFrame: this.currentFrame,
      playing: !this.audio.paused,
      buffering: this.buffering,
      error: this.errorMessage,
    });
  }
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

function playbackErrorMessage(error: unknown): string {
  if (error instanceof DOMException && error.name === "NotAllowedError")
    return "この端末で音声再生が許可されませんでした。もう一度再生を押してください。";
  if (error instanceof DOMException && error.name === "NotSupportedError")
    return "この位置の音声を再生できませんでした。もう一度再生すると音声を再読み込みします。";
  return "音声を再生できませんでした。もう一度再生すると音声を再読み込みします。";
}

function mediaElementErrorMessage(error: MediaError | null): string {
  if (error?.code === MediaError.MEDIA_ERR_DECODE)
    return "音声デコーダが停止しました。もう一度再生すると音声を再読み込みします。";
  if (error?.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED)
    return "この音声を再生できませんでした。音声を選び直すか、もう一度再生してください。";
  return "音声の読み込みが停止しました。もう一度再生すると音声を再読み込みします。";
}
